import React, { useState } from 'react';
import moment from 'moment';
import { useNavigate } from "react-router-dom";
import { FaHome, FaChevronLeft, FaChevronRight, FaUsers, FaClock, FaCalendarCheck } from 'react-icons/fa';

const stringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 360;
    return `linear-gradient(135deg, hsl(${hue}, 28%, 93%), hsl(${(hue + 25) % 360}, 28%, 90%))`;
};

const Calendar = ({ classSchedule, onClassSelect }) => {
    const [currentDate, setCurrentDate] = useState(moment());
    const [view, setView] = useState('list'); // 'month', 'week'
    const navigate = useNavigate();

    const changeMonth = (delta) => {
        setCurrentDate(currentDate.clone().add(delta, 'month'));
    };

    const changeWeek = (delta) => {
        setCurrentDate(currentDate.clone().add(delta, 'week'));
    };
    const changeListWeek = (delta) => {
        setCurrentDate(currentDate.clone().add(delta, 'week'));
    };
    const handleHomeClick = () => {
        navigate("/");
    };

    const renderCalendar = () => {
        const startDate = view === 'week'
            ? currentDate.clone().startOf('week')
            : currentDate.clone().startOf('month').startOf('week');
        const endDate = view === 'week'
            ? currentDate.clone().endOf('week')
            : currentDate.clone().endOf('month').endOf('week');

        const calendar = [];
        const day = startDate.clone();

        while (day.isBefore(endDate)) {
            calendar.push(
                Array(7).fill(0).map(() => {
                    const clonedDate = day.clone();
                    day.add(1, 'day');
                    return clonedDate;
                })
            );
        }

        if (view === 'list') {
            const startOfWeek = currentDate.clone().startOf('week');

            return (
              <div className="space-y-3">
                {Array(7).fill(0).map((_, index) => {
                  const day = startOfWeek.clone().add(index, 'days');
                  const isToday = day.isSame(moment(), 'day');
                  const classesForDay = classSchedule.filter(cls =>
                    cls.days.includes(day.format('dddd')) ||
                    (cls.isSpecialClass && cls.specialClassDate === day.format('YYYY-MM-DD'))
                  ).sort((a, b) => moment(a.startTime, 'HH:mm') - moment(b.startTime, 'HH:mm'));

                  return (
                    <div key={day.format('YYYY-MM-DD')} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                      <div className={`px-5 py-3 flex items-center gap-2.5 ${isToday ? 'bg-indigo-600' : 'bg-slate-800'}`}>
                        <FaCalendarCheck className="text-white/60 text-sm shrink-0" />
                        <h3 className="text-sm font-bold text-white tracking-wide">
                          {day.format('dddd, MMMM D')}
                        </h3>
                        {isToday && <span className="ml-auto text-xs font-semibold bg-white/20 text-white rounded-full px-2 py-0.5">Today</span>}
                      </div>
                      <div className="p-4">
                        {classesForDay.length > 0 ? (
                          <div className="space-y-2.5">
                            {classesForDay.map(cls => {
                              const bookingsForDate = cls.bookings?.[day.format('YYYY-MM-DD')] || { bookedSpots: 0 };
                              const bookedSpots = bookingsForDate.bookedSpots;
                              const availableSpots = cls.availableSpots - bookedSpots;

                              return (
                                <div key={cls.id} className="border border-gray-100 rounded-xl p-4 hover:shadow-sm transition-shadow duration-200" style={{ background: stringToColor(cls.id) }}>
                                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div className="flex-grow">
                                      <h4 className="font-bold text-gray-800 text-base mb-1">{cls.className}</h4>
                                      <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-1">
                                        <FaClock size={10} />
                                        <span className="font-medium">{cls.startTime} – {cls.endTime}</span>
                                      </div>
                                      {cls.coaches && cls.coaches.length > 0 && (
                                        <div className="flex items-center gap-1.5 text-gray-500 text-xs">
                                          <FaUsers size={10} />
                                          <span>{cls.coaches.join(', ')}</span>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-4">
                                      <div className="flex gap-3 text-center">
                                        <div>
                                          <div className="font-bold text-slate-700 text-base">{bookedSpots}</div>
                                          <div className="text-xs text-gray-400 uppercase tracking-wide">Booked</div>
                                        </div>
                                        <div className="w-px bg-gray-200 self-stretch" />
                                        <div>
                                          <div className="font-bold text-emerald-600 text-base">{availableSpots}</div>
                                          <div className="text-xs text-gray-400 uppercase tracking-wide">Free</div>
                                        </div>
                                        <div className="w-px bg-gray-200 self-stretch" />
                                        <div>
                                          <div className="font-bold text-gray-500 text-base">{cls.availableSpots}</div>
                                          <div className="text-xs text-gray-400 uppercase tracking-wide">Total</div>
                                        </div>
                                      </div>
                                      <button
                                        className="bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white px-5 py-2 rounded-lg text-sm font-bold transition-all duration-200 shadow-sm"
                                        onClick={() => onClassSelect(cls, day.format('YYYY-MM-DD'))}
                                      >
                                        Book
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-slate-300 text-sm text-center py-6">No classes scheduled</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          }
        
        return (
            <div className="overflow-x-auto rounded-2xl shadow-sm border border-gray-100">
                <table className="w-full border-collapse min-w-[768px] bg-white">
                    <thead>
                        <tr className="bg-slate-800">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                                <th key={d} className="p-3 text-white/70 font-semibold text-xs tracking-wider uppercase border-r border-white/5 last:border-r-0">{d}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {calendar.map((week, i) => (
                            <tr key={i} className="border-b border-gray-100">
                                {week.map(date => {
                                    const isToday = date.isSame(moment(), 'day');
                                    const isCurrentMonth = date.month() === currentDate.month();

                                    return (
                                        <td key={date.format('YYYY-MM-DD')} className={`p-2 border-r border-gray-100 last:border-r-0 align-top min-w-[100px] ${
                                            isToday ? 'bg-indigo-50/60' : isCurrentMonth ? 'bg-white' : 'bg-slate-50'
                                        }`}>
                                            <div className={`text-xs font-bold mb-2 w-6 h-6 flex items-center justify-center rounded-full ${
                                                isToday ? 'bg-indigo-600 text-white' : isCurrentMonth ? 'text-gray-700' : 'text-gray-300'
                                            }`}>
                                                {date.format('D')}
                                            </div>
                                            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                                                {classSchedule
                                                    .filter(cls => cls.days.includes(date.format('dddd')) || (cls.isSpecialClass && cls.specialClassDate === date.format('YYYY-MM-DD')))
                                                    .sort((a, b) => moment(a.startTime, 'HH:mm') - moment(b.startTime, 'HH:mm'))
                                                    .map(cls => {
                                                        const isPast = date.isBefore(moment(), 'day');
                                                        const colorGradient = isPast ? 'linear-gradient(135deg, #f1f5f9, #e2e8f0)' : stringToColor(cls.id);

                                                        const bookingsForDate = cls.bookings?.[date.format('YYYY-MM-DD')] || { bookedSpots: 0, availableSpots: cls.availableSpots };
                                                        const attendanceForDate = cls.attendance?.[date.format('YYYY-MM-DD')] || [];
                                                        const bookedSpots = bookingsForDate.bookedSpots ?? 0;
                                                        const attendedSpots = attendanceForDate.length;
                                                        const totalSpots = cls.availableSpots;
                                                        const remainingSpots = totalSpots - Math.max(bookedSpots, attendedSpots);

                                                        return (
                                                            <div
                                                                key={cls.id}
                                                                className="rounded-lg p-2 border border-black/5 hover:shadow-sm transition-shadow"
                                                                style={{ background: colorGradient }}
                                                            >
                                                                <div className={`font-semibold text-xs mb-0.5 ${isPast ? 'text-gray-400' : 'text-gray-800'}`}>{cls.className}</div>
                                                                <div className={`text-xs mb-1.5 flex items-center gap-1 ${isPast ? 'text-gray-400' : 'text-gray-500'}`}>
                                                                    <FaClock size={9} />
                                                                    {cls.startTime}–{cls.endTime}
                                                                </div>
                                                                {cls.coaches && cls.coaches.length > 0 && (
                                                                    <div className={`hidden sm:flex items-center gap-1 text-xs mb-1.5 ${isPast ? 'text-gray-400' : 'text-gray-500'}`}>
                                                                        <FaUsers size={9} />
                                                                        {cls.coaches.join(', ')}
                                                                    </div>
                                                                )}
                                                                <div className="flex flex-wrap gap-1 mb-1.5">
                                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-white/60 text-gray-600">
                                                                        {attendedSpots}/{totalSpots} in
                                                                    </span>
                                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-white/60 text-gray-600">
                                                                        {bookedSpots} bkd
                                                                    </span>
                                                                </div>
                                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold mb-1.5 ${
                                                                    remainingSpots > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                                                                }`}>
                                                                    {remainingSpots >= 0 ? `${remainingSpots} left` : 'Full'}
                                                                </span>
                                                                {!isPast && remainingSpots > 0 && (
                                                                    <button
                                                                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-2 py-1 rounded-md text-xs font-semibold transition-colors duration-200"
                                                                        onClick={() => onClassSelect(cls, date.format('YYYY-MM-DD'))}
                                                                    >
                                                                        Book
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="bg-slate-50 p-4 sm:p-6 rounded-2xl">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3 mb-4">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
                    <button
                        onClick={handleHomeClick}
                        className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200"
                    >
                        <FaHome size={13} />
                        Home
                    </button>

                    <div className="flex items-center gap-2">
                        {view === 'week' ? (
                            <>
                                <button onClick={() => changeWeek(-1)} className="bg-slate-100 hover:bg-slate-200 text-slate-600 p-2 rounded-lg transition-colors duration-200">
                                    <FaChevronLeft size={12} />
                                </button>
                                <h3 className="text-sm font-bold text-gray-800 min-w-[160px] text-center">{currentDate.format('MMM YYYY [W]w')}</h3>
                                <button onClick={() => changeWeek(1)} className="bg-slate-100 hover:bg-slate-200 text-slate-600 p-2 rounded-lg transition-colors duration-200">
                                    <FaChevronRight size={12} />
                                </button>
                            </>
                        ) : view === 'list' ? (
                            <>
                                <button onClick={() => changeListWeek(-1)} className="bg-slate-100 hover:bg-slate-200 text-slate-600 p-2 rounded-lg transition-colors duration-200">
                                    <FaChevronLeft size={12} />
                                </button>
                                <h3 className="text-sm font-bold text-gray-800 min-w-[160px] text-center">{currentDate.format('MMM YYYY [W]w')}</h3>
                                <button onClick={() => changeListWeek(1)} className="bg-slate-100 hover:bg-slate-200 text-slate-600 p-2 rounded-lg transition-colors duration-200">
                                    <FaChevronRight size={12} />
                                </button>
                            </>
                        ) : (
                            <>
                                <button onClick={() => changeMonth(-1)} className="bg-slate-100 hover:bg-slate-200 text-slate-600 p-2 rounded-lg transition-colors duration-200">
                                    <FaChevronLeft size={12} />
                                </button>
                                <h3 className="text-sm font-bold text-gray-800 min-w-[160px] text-center">{currentDate.format('MMMM YYYY')}</h3>
                                <button onClick={() => changeMonth(1)} className="bg-slate-100 hover:bg-slate-200 text-slate-600 p-2 rounded-lg transition-colors duration-200">
                                    <FaChevronRight size={12} />
                                </button>
                            </>
                        )}
                    </div>

                    <select
                        className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold text-gray-700 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-300 focus:outline-none transition-colors duration-200"
                        value={view}
                        onChange={(e) => setView(e.target.value)}
                    >
                        <option value="month">Month</option>
                        <option value="week">Week</option>
                        <option value="list">List</option>
                    </select>
                </div>
            </div>

            {renderCalendar()}
        </div>
    );
};

export default Calendar;